import { csvCell } from "@/lib/csv/csvCell";
import { TOOL_MAX_BULK_LINES, TOOL_MAX_LINE_LENGTH } from "@/lib/tools/limits";
import {
  compactIban,
  czechAccountToIban,
  formatCzechAccount,
  formatIban,
  ibanToCzechAccount,
  isValidCzechAccount,
  isValidIban,
  parseCzechAccount,
} from "./czechAccount";
import { czechBank } from "./czechBanks";

/**
 * The free account ↔ IBAN converter (`/prevod-cisla-uctu-na-iban`): one line
 * of input to both forms of the account, the bank from the ČNB code list, and
 * whether the check digits hold. Pure and offline.
 */

export type ConvertError =
  "too_long" | "unrecognised" | "account_check" | "iban_check" | "iban_foreign" | "unknown_bank";

export interface ConvertResult {
  input: string;
  /** `19-2000145399/0800`, or "" when the input could not be read. */
  account: string;
  /** Compact IBAN, or "". */
  iban: string;
  bankCode: string;
  bankName: string;
  bic: string;
  /** The check digits hold and the bank code is on the ČNB list. */
  valid: boolean;
  error: ConvertError | null;
}

function result(input: string, partial: Partial<ConvertResult>): ConvertResult {
  return {
    input,
    account: "",
    iban: "",
    bankCode: "",
    bankName: "",
    bic: "",
    valid: false,
    error: null,
    ...partial,
  };
}

function withBank(r: ConvertResult): ConvertResult {
  const bank = r.bankCode ? czechBank(r.bankCode) : undefined;
  if (!bank) return { ...r, valid: false, error: r.error ?? "unknown_bank" };
  return { ...r, bankName: bank.name, bic: bank.bic };
}

export function convertAccount(raw: string): ConvertResult {
  const input = raw.trim();
  if (input.length > TOOL_MAX_LINE_LENGTH) return result(input.slice(0, 20), { error: "too_long" });
  const compact = input.replace(/\s+/g, "");

  if (/^[A-Za-z]{2}\d{2}/.test(compact)) {
    const iban = compactIban(compact);
    if (!iban.startsWith("CZ")) {
      return result(input, { iban, valid: isValidIban(iban), error: "iban_foreign" });
    }
    if (!isValidIban(iban)) return result(input, { iban, error: "iban_check" });
    const account = ibanToCzechAccount(iban);
    if (!account) return result(input, { iban, error: "iban_check" });
    const accountValid = isValidCzechAccount(account);
    return withBank(
      result(input, {
        iban,
        account: formatCzechAccount(account),
        bankCode: account.bankCode,
        valid: accountValid,
        error: accountValid ? null : "account_check",
      })
    );
  }

  const account = parseCzechAccount(input);
  if (!account) return result(input, { error: "unrecognised" });
  const accountValid = isValidCzechAccount(account);
  const converted = withBank(
    result(input, {
      account: formatCzechAccount(account),
      bankCode: account.bankCode,
      iban: czechAccountToIban(account),
      valid: accountValid,
      error: accountValid ? null : "account_check",
    })
  );
  // No IBAN for a number that fails its check or a bank code that does not
  // exist: it would look usable and send money nowhere.
  return converted.valid ? converted : { ...converted, iban: "" };
}

export interface BulkResult {
  rows: ConvertResult[];
  /** Lines past the cap, not converted. */
  skipped: number;
}

/** One account or IBAN per line; empty lines are ignored. */
export function convertBulk(text: string): BulkResult {
  const lines = text
    .split(/\r\n|\r|\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  return {
    rows: lines.slice(0, TOOL_MAX_BULK_LINES).map(convertAccount),
    skipped: Math.max(0, lines.length - TOOL_MAX_BULK_LINES),
  };
}

/** Short reasons for the CSV's error column. */
const ERROR_LABELS: Record<"cs" | "en", Record<ConvertError, string>> = {
  cs: {
    too_long: "příliš dlouhý řádek",
    unrecognised: "není číslo účtu ani IBAN",
    account_check: "číslo účtu neprošlo kontrolou (modulo 11)",
    iban_check: "IBAN neprošel kontrolou (modulo 97)",
    iban_foreign: "zahraniční IBAN, bez českého čísla účtu",
    unknown_bank: "kód banky není v číselníku ČNB",
  },
  en: {
    too_long: "line too long",
    unrecognised: "neither an account number nor an IBAN",
    account_check: "account number fails its check (modulo 11)",
    iban_check: "IBAN fails its check (modulo 97)",
    iban_foreign: "foreign IBAN, no Czech account number",
    unknown_bank: "bank code not on the CNB list",
  },
};

const HEADERS = {
  cs: ["Zadáno", "Číslo účtu", "IBAN", "Kód banky", "Banka", "BIC", "Platné", "Chyba"],
  en: ["Input", "Account number", "IBAN", "Bank code", "Bank", "BIC", "Valid", "Error"],
} as const;

/**
 * The bulk result as CSV: semicolons in Czech, commas in English, a BOM for
 * Excel, and every cell through csvCell (quoted, formula starts neutralised).
 */
export function bulkToCsv(rows: readonly ConvertResult[], lang: "cs" | "en"): string {
  const sep = lang === "cs" ? ";" : ",";
  const yes = lang === "cs" ? "ano" : "yes";
  const no = lang === "cs" ? "ne" : "no";
  const out = [HEADERS[lang].map((h) => csvCell(h)).join(sep)];
  for (const r of rows) {
    out.push(
      [
        r.input,
        r.account,
        r.iban ? formatIban(r.iban) : "",
        r.bankCode,
        r.bankName,
        r.bic,
        r.valid ? yes : no,
        r.error ? ERROR_LABELS[lang][r.error] : "",
      ]
        .map((cell) => csvCell(cell))
        .join(sep)
    );
  }
  return `${String.fromCharCode(0xfeff)}${out.join("\r\n")}\r\n`;
}
